import React, { useEffect, useState } from 'react';
import { MessageSquareIcon, XIcon, SendIcon } from 'lucide-react';
import { Button } from './Button';
import { submitHelpRequest } from '../services/web3FormsService';
export function HelpChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<{
    text: string;
    fromUser: boolean;
    timestamp: Date;
  }[]>([]);
  const [hasNotification, setHasNotification] = useState(false);
  const [isSending, setIsSending] = useState(false);
  // Welcome message when chat is first opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        text: 'Hi! How can I help you with the SponsrAI platform today?',
        fromUser: false,
        timestamp: new Date()
      }]);
    }
    if (isOpen && hasNotification) {
      setHasNotification(false);
    }
  }, [isOpen, messages.length, hasNotification]);
  // Simulate receiving a help notification after 30 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen) {
        setHasNotification(true);
      }
    }, 30000);
    return () => clearTimeout(timer);
  }, [isOpen]);
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    // Add user message
    const userMessage = {
      text: message,
      fromUser: true,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsSending(true);
    // Submit to Web3Forms
    try {
      await submitHelpRequest({
        message: message,
        timestamp: new Date().toISOString(),
        page: window.location.pathname
      });
    } catch (error) {
      console.error('Failed to send chat message:', error);
    }
    setMessage('');
    // Simulate response after a short delay
    setTimeout(() => {
      setIsSending(false);
      let responseText = '';
      if (message.toLowerCase().includes('contract') || message.toLowerCase().includes('agreement')) {
        responseText = 'To create a sponsorship agreement, first accept a match and then click "Create Sponsorship Agreement". Both parties must approve the agreement for it to become active.';
      } else if (message.toLowerCase().includes('match') || message.toLowerCase().includes('matching')) {
        responseText = 'Our AI algorithm matches brands and organizers based on target audience, industry, budget, and several other factors. The more information you provide in your profile, the better your matches will be!';
      } else if (message.toLowerCase().includes('test') || message.toLowerCase().includes('panel') || message.toLowerCase().includes('sampling')) {
        responseText = 'Brands can specify information about upcoming test panels or sampling activities in their profile. This helps us match them with suitable event organizers.';
      } else if (message.toLowerCase().includes('pay') || message.toLowerCase().includes('price') || message.toLowerCase().includes('cost')) {
        responseText = 'Payment terms are determined between the brand and organizer and documented in the sponsorship agreement. The platform currently does not charge any fees for matches.';
      } else {
        responseText = 'Thank you for your question! Your message has been sent to our support team. One of our team members will get back to you shortly.';
      }
      const botResponse = {
        text: responseText,
        fromUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }, 1000);
  };
  return <div className="fixed bottom-4 right-4 z-50" data-id="element-540">
      {isOpen ? <div className="bg-white rounded-xl shadow-2xl w-80 md:w-96 flex flex-col border border-gray-200" style={{
      height: '420px'
    }} data-id="element-541">
          {/* Header */}
          <div className="bg-blue-600 text-white p-4 rounded-t-xl flex justify-between items-center" data-id="element-542">
            <div data-id="element-543">
              <h3 className="font-semibold" data-id="element-544">Support</h3>
              <p className="text-xs text-blue-100" data-id="element-545">
                We typically reply within a few hours
              </p>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors" data-id="element-546">
              <XIcon className="h-5 w-5" data-id="element-547" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" data-id="element-548">
            {messages.map((msg, index) => <div key={index} className={`flex ${msg.fromUser ? 'justify-end' : 'justify-start'}`} data-id="element-549">
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${msg.fromUser ? 'bg-blue-600 text-white rounded-br-md' : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'}`} data-id="element-550">
                  <p className="text-sm leading-relaxed" data-id="element-551">{msg.text}</p>
                  <div className={`text-xs mt-1 ${msg.fromUser ? 'text-blue-200' : 'text-gray-400'}`} data-id="element-552">
                    {msg.timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </div>
                </div>
              </div>)}
            {isSending && <div className="flex justify-start" data-id="element-553">
                <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100" data-id="element-554">
                  <div className="flex space-x-1" data-id="element-555">
                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-556"></div>
                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-557"></div>
                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-558"></div>
                  </div>
                </div>
              </div>}
          </div>

          {/* Input */}
          <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-3 flex gap-2 bg-white rounded-b-xl" data-id="element-559">
            <input type="text" placeholder="Type a message..." className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={message} onChange={e => setMessage(e.target.value)} disabled={isSending} data-id="element-560" />
            <Button type="submit" variant="primary" disabled={isSending || !message.trim()} className="rounded-xl px-4" data-id="element-561">
              <SendIcon className="h-4 w-4" data-id="element-562" />
            </Button>
          </form>
        </div> : <button onClick={() => setIsOpen(true)} className="bg-blue-600 text-white rounded-full p-4 shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all relative group" data-id="element-563">
          <MessageSquareIcon className="h-6 w-6" data-id="element-564" />
          {hasNotification && <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs font-medium animate-pulse" data-id="element-565">
              1
            </span>}
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-566">
            Need help?
          </span>
        </button>}
    </div>;
}